import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { CreditTransaction } from "@/lib/credit-types";

export function useCreditTransactions(limit: number = 10) {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTransactions = useCallback(async () => {
    if (!user) {
      setTransactions([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("credit_transactions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;

      setTransactions(
        (data || []).map((t) => ({
          id: t.id,
          userId: t.user_id,
          orgId: t.org_id,
          amount: t.amount,
          balanceAfter: t.balance_after,
          source: t.source,
          runId: t.run_id,
          metadata: (t.metadata as Record<string, unknown>) || {},
          createdAt: t.created_at,
        }))
      );
    } catch (error) {
      console.error("Failed to fetch credit transactions:", error);
    } finally {
      setLoading(false);
    }
  }, [user, limit]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return {
    transactions,
    loading,
    refetch: fetchTransactions,
  };
}
